import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
} from '@mui/material';
import { Box, Button, Typography } from 'components/index';
import { PredictedLineItemWithDivision, Division } from '../glossary/glossary.type';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import theme from 'assets/theme';
import { Close } from '@mui/icons-material';
import { formatCurrency } from '../../common/utils/common.util';
import { usePrompt } from '../AppPrompt';

interface AutoAssignDivisionsDialogProps {
  open: boolean;
  onClose: () => void;
  lineItems: PredictedLineItemWithDivision[];
  divisions: Division[];
  updateDivisions: (updatedLineItems: PredictedLineItemWithDivision[]) => void;
  useTotalBudget?: boolean;
  showToggle?: boolean;
}

const noRowsOverlay = () => (
  <Box display={'flex'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
    <Typography color='blue'>No Data to Display!</Typography>
  </Box>
);

const AutoAssignDivisionsDialog: React.FC<AutoAssignDivisionsDialogProps> = ({
  open,
  onClose,
  lineItems,
  divisions,
  updateDivisions,
  useTotalBudget,
  showToggle,
}) => {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allLocalLineItems, setAllLocalLineItems] = useState<PredictedLineItemWithDivision[]>([]);
  const [hideContext, setHideContext] = useState(false);
  const { getConfirmation } = usePrompt();

  // allLocalLineItems is the single source of truth — reset only when the dialog opens or lineItems prop changes.
  // Only the suggested items (previously unassigned line items the predictor gave a division to) start checked;
  // pre-assigned ones are shown for context but left unchecked.
  useEffect(() => {
    if (open) {
      setAllLocalLineItems(lineItems);
      setSelectionModel(lineItems.filter(item => item.division && !item.preAssignedDivision).map(item => item.id));
    }
  }, [open, lineItems]);

  // localLineItems is a derived display view — filtering never mutates the underlying data
  const localLineItems = useMemo(
    () => (hideContext ? allLocalLineItems.filter(item => !item.preAssignedDivision) : allLocalLineItems),
    [allLocalLineItems, hideContext],
  );

  // Divisions that have no line items assigned or whose total revised budget is $0.
  // totalBudget === 0 intentionally catches divisions where credits/reductions cancel out charges.
  const emptyDivisions = useMemo(() => {
    return divisions.filter(division => {
      const assigned = allLocalLineItems.filter(item => item.division?.id === division.id);
      if (assigned.length === 0) return true;
      const totalBudget = assigned.reduce((sum, item) => sum + (item.revisedBudget ?? 0), 0);
      return totalBudget === 0;
    });
  }, [divisions, allLocalLineItems]);

  // Derived from allLocalLineItems so inline division edits (and rows on other pages) are always reflected.
  const selectedLineItems = useMemo(
    () => allLocalLineItems.filter(item => selectionModel.includes(item.id)),
    [allLocalLineItems, selectionModel],
  );

  const handleDivisionChange = (lineItemId: string, newDivisionId: string) => {
    setAllLocalLineItems(prev =>
      prev.map(item =>
        item.id === lineItemId
          ? { ...item, division: divisions.find(d => d.id === newDivisionId) || null, preAssignedDivision: false }
          : item,
      ),
    );
  };

  const handleConfirm = async () => {
    if (selectedLineItems.length === 0) {
      toast.error('Please select at least one line item');
      return;
    }

    // Only send items that weren't previously assigned to the backend
    const unassignedItems = selectedLineItems.filter(item => !item.preAssignedDivision);
    if (unassignedItems.length === 0) {
      toast.error('Please select at least one unassigned line item');
      return;
    }

    if (emptyDivisions.length > 0) {
      const isConfirmed = await getConfirmation({
        heading: 'Divisions Without Line Items',
        description: `${emptyDivisions.length} division(s) have no line items assigned. Would you like to go back and check, or continue anyway?`,
        confirmButton: {
          label: 'Continue Anyway',
          color: 'error',
        },
        cancelButton: {
          label: 'Go Back',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;
    }

    updateDivisions(unassignedItems);
    setAllLocalLineItems([]);
    setSelectionModel([]);
    onClose();
  };

  const handleDialogClose = () => {
    setAllLocalLineItems([]);
    setSelectionModel([]);
    onClose();
  };

  const renderDivisionDropdown = (params: any) => {
    const { id, division } = params.row;

    return (
      <FormControl fullWidth>
        <InputLabel></InputLabel>
        <Select
          value={division?.id || ''}
          onChange={e => handleDivisionChange(id, e.target.value as string)}
          renderValue={selected => {
            const selectedDivision = divisions.find(d => d.id === selected);
            return <Box padding='4px 0px'>{`${selectedDivision?.code || ''} ${selectedDivision?.name || ''}`}</Box>;
          }}
        >
          {divisions.map(division => (
            <MenuItem key={division.id} value={division.id}>
              {`${division.code} ${division.name}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'description',
      headerName: 'Line Item',
      flex: 1,
    },
    {
      field: 'division',
      headerName: 'Division',
      flex: 1,
      renderCell: renderDivisionDropdown,
    },
    ...(hideContext
      ? []
      : [
          {
            field: 'revisedBudget',
            headerName: useTotalBudget ? 'Total Budget' : 'Revised Budget',
            flex: 1,
            valueFormatter: (params: any) => {
              return formatCurrency(params?.toString(), true);
            },
          },
        ]),
  ];

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth='lg' fullWidth PaperProps={{ sx: { minHeight: '75vh' } }}>
      <DialogTitle component='div' sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant='h5'>{hideContext ? 'Confirm New Divisions?' : 'Review All Line Items'}</Typography>
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          {showToggle && (
            <Tooltip title='Toggle to hide pre-assigned lineitems and revised budget' arrow>
              <FormControlLabel
                control={<Switch checked={hideContext} onChange={e => setHideContext(e.target.checked)} />}
                label='Hide Context'
              />
            </Tooltip>
          )}
          <IconButton onClick={handleDialogClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={dialogContentStyles}>
        {emptyDivisions.length > 0 && (
          <Alert severity='warning' sx={{ mb: 2, flexShrink: 0, maxHeight: 160, overflowY: 'auto', alignItems: 'flex-start' }}>
            <strong>No line items in the following divisions — check before confirming:</strong>
            <Box
              component='ul'
              sx={{
                mt: 0.5,
                mb: 0,
                pl: 2.5,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 6,
                '& li': { pr: 2 },
              }}
            >
              {emptyDivisions.map(d => (
                <li key={d.id}>{`${d.code} — ${d.name}`}</li>
              ))}
            </Box>
          </Alert>
        )}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {localLineItems.length > 0 ? (
            <DataGrid
              columns={columns}
              rows={localLineItems}
              getRowId={row => row.id}
              checkboxSelection
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={setSelectionModel}
              keepNonExistentRowsSelected
              initialState={{ pagination: { paginationModel: { pageSize: 100, page: 0 } } }}
              pageSizeOptions={[25, 50, 100]}
              getRowClassName={params => (params.row.preAssignedDivision ? '' : 'unassigned-row')}
              sx={{ ...dataGridStyles, flex: 1, minHeight: 0 }}
            />
          ) : (
            <Box display={'flex'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
              <Typography color='blue'>No Data to Display!</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant='outlined' onClick={handleDialogClose} color='secondary'>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={selectedLineItems.length === 0}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const dialogContentStyles = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: 500,
  maxHeight: 'calc(75vh - 130px)',
  p: 2,
};

const dataGridStyles = {
  minHeight: 450,
  '& .MuiDataGrid-columnHeader': {
    fontWeight: 'bold',
    borderRadius: '0',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-cellCheckbox': {
    paddingLeft: 1,
  },
  '& .unassigned-row': {
    backgroundColor: '#fff3e0',
  },
  '& .pre-assigned-row': {
    backgroundColor: '#e3f2fd',
  },
};

export default React.memo(AutoAssignDivisionsDialog);
